import { useEffect, useMemo, useState } from 'react'
import { DateTime, Interval } from 'luxon'
import { LuCircleArrowLeft, LuCircleArrowRight } from 'react-icons/lu'

import { User } from '@/lib/core/interfaces/user.types'
import { Organization } from '@/lib/core/interfaces/organization.types'
import { TypeBadgeFilled } from '@/lib/core/components/TypeBadgeFilled'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'

import { useActiveShares } from '@/lib/features/SmartShares/api'
import { BridgeStatus } from '@/lib/features/Assets/interfaces'
import { Share } from '@/lib/features/SmartShares/interfaces'

import styles from './ShareContactDetails.module.css'

export enum Type {
  IN,
  OUT,
}

export type ShareContactDetailsProps = {
  assetId: number
  organization?: Organization
  textColor?: string
  contactFrom?: User
  contactTo?: User
  // BridgeStatus is used as an identity flag that the entry is from HistoryTable and
  // shouldn't use looked up data, instead using the contactFrom/To and Organization to show specific details
  bridgeStatus?: BridgeStatus
  onChangeShare?: (share: Share) => void
}

export const ShareContactDetails = ({
  assetId,
  organization,
  contactFrom,
  contactTo,
  bridgeStatus,
  onChangeShare,
  textColor = '#fff',
}: ShareContactDetailsProps) => {
  const { data } = useActiveShares()

  const inShare = useMemo(
    () =>
      data?.results.find((share) => share.bridgeStatus === 'borrowed' && share.assetId === assetId),
    [data?.results],
  )

  const outShares = useMemo(
    () =>
      data?.results.filter((share) => {
        return (
          share.bridgeStatus === 'lent' &&
          share.assetId === assetId &&
          Interval.fromDateTimes(
            DateTime.fromISO(share.dateFrom),
            DateTime.fromISO(share.dateTo),
          ).contains(DateTime.local())
        )
      }),
    [data?.results],
  )

  const outShare = useMemo(
    () =>
      data?.results.find((share) => {
        return (
          share.bridgeStatus === 'lent' &&
          share.assetId === assetId &&
          Interval.fromDateTimes(
            DateTime.fromISO(share.dateFrom),
            DateTime.fromISO(share.dateTo),
          ).contains(DateTime.local())
        )
      }),
    [data?.results],
  )

  const [page, setPage] = useState(0)

  useEffect(() => {
    if (outShares.length) {
      onChangeShare?.(outShares[page])
    }
  }, [page, outShares, onChangeShare])

  return (
    <>
      <div className={styles.container}>
        {bridgeStatus === BridgeStatus.BORROWED && (
          <ContactDetails
            textColor={textColor}
            status={Type.IN}
            contact={contactFrom}
            organization={organization}
          />
        )}

        {bridgeStatus === BridgeStatus.LENT && (
          <ContactDetails
            textColor={textColor}
            status={Type.OUT}
            contact={contactTo}
            organization={organization}
          />
        )}

        {!bridgeStatus && (inShare?.contactFrom || inShare?.organization) && (
          <ContactDetails
            textColor={textColor}
            status={Type.IN}
            contact={inShare?.contactFrom}
            organization={inShare?.organization}
          />
        )}

        {!bridgeStatus && (outShare?.contactTo || outShare?.organization) && (
          <ContactDetails
            textColor={textColor}
            status={Type.OUT}
            contact={outShares.length > 1 ? outShares[page]?.contactTo : outShare?.contactTo}
            organization={
              outShares.length > 1 ? outShares[page]?.organization : outShare?.organization
            }
          />
        )}
      </div>
      {outShares.length > 1 && (
        <div className={styles.pagination}>
          <button
            type={'button'}
            className={styles.paginationButton}
            onClick={() => {
              setPage(Math.max(page - 1, 0))
            }}
          >
            <LuCircleArrowLeft size={26} color={textColor} strokeWidth={1.5} />
          </button>
          <div className={styles.paginationText}>
            {page + 1} of {outShares.length}
          </div>
          <button
            type={'button'}
            className={styles.paginationButton}
            onClick={() => {
              setPage(Math.min(page + 1, outShares.length - 1))
            }}
          >
            <LuCircleArrowRight size={26} color={textColor} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </>
  )
}

export type ContactDetailsProps = {
  status: Type
  contact: User | undefined
  organization: Organization | undefined
  textColor: string
}
export const ContactDetails = ({
  status,
  contact,
  organization,
  textColor,
}: ContactDetailsProps) => {
  const color = status === Type.IN ? '#579733' : '#E18B3B'
  return (
    <div className={styles.innerContainer} style={{ borderColor: color }}>
      {organization && (
        <OrganizationDetails textColor={textColor} status={status} organization={organization} />
      )}
      {contact && <UserDetails contact={contact} textColor={textColor} />}
    </div>
  )
}

export type OrganizationDetailsProps = {
  organization: Organization
  status: Type
  textColor: string
}

export const OrganizationDetails = ({
  organization,
  status,
  textColor,
}: OrganizationDetailsProps) => {
  return (
    <div>
      {status === Type.IN && (
        <div className={styles.statusDetails}>
          <span className={styles.header} style={{ color: textColor }}>
            Received from:
          </span>{' '}
          <TypeBadgeFilled type={Type.IN} />
        </div>
      )}
      {status === Type.OUT && (
        <div className={styles.statusDetails}>
          <span className={styles.header} style={{ color: textColor }}>
            Shared With:
          </span>
          <TypeBadgeFilled type={Type.OUT} />
        </div>
      )}
      <p className={styles.profile}>
        <CircleAvatar src={organization.avatar} placeholder="empty" size={27.5} />

        {organization.name}
      </p>
    </div>
  )
}

export type UserDetailsProps = {
  contact: User
  textColor: string
}

export const UserDetails = ({ contact, textColor }: UserDetailsProps) => (
  <div>
    <span className={styles.header} style={{ color: textColor }}>
      Contact:
    </span>
    <div className={styles.userDetailsContent} style={{ color: textColor }}>
      <span style={{ wordBreak: 'break-all' }}>
        {contact.firstName} {contact.lastName}
      </span>
      <span>{contact.phoneNumber}</span>
      <span style={{ wordBreak: 'break-all' }}>{contact.email}</span>
    </div>
  </div>
)
